import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  corporationData: {
    channels: [],
    catalogs: [],
    categories: [],
    attributes: [],
    alergens: [],
    options: [],
    corporationOwner: {},
    info: {},
  },
  userConfig: {
    loading: false,
    logged: false,
    userData: {},
    platformId: null,
    language: "es",
    literals: [],
    rol: null,
    token: null,
    componentActive: "dashboard",
    datatable: {
      configuration: { maxRows: 12, indexStart: 0, pages: [0], filterBy: { section: "", filter: [] } },
      requestQuery: {},
      refreshVersion: 0,
      tables: {},
    },
    alerts: [],
    floatPanel: {
      visible: false,
      queryId: null,
      isEmpty: true,
      data: {},
    },
    order: {},
    cart: [],
  },
};

const userConfigSlice = createSlice({
  name: "aplicationConfig",
  initialState,
  reducers: {
    setConfigurationSearch: (state, action) => { state.userConfig.configurationSearch = action.payload; },
    setDiscountSearch: (state, action) => { state.userConfig.discountSearch = action.payload; },
    setClientSearch: (state, action) => { state.userConfig.clientSearch = action.payload; },
    setAttributeSearch: (state, action) => { state.userConfig.attributeSearch = action.payload; },
    setCategorySearch: (state, action) => { state.userConfig.categorySearch = action.payload; },
    setCatalogSearch: (state, action) => { state.userConfig.catalogSearch = action.payload; },
    setCartSearch: (state, action) => { state.userConfig.cartSearch = action.payload; },
    setOrderSearch: (state, action) => { state.userConfig.orderSearch = action.payload; },
    setChannelSearch: (state, action) => { state.userConfig.channelSearch = action.payload; },
    setLoading: (state, action) => {
      state.userConfig.loading = action.payload;
    },
    setCorporationData: (state, action) => {
      state.corporationData.info = action.payload;
    },
    updateCorporationData: (state, action) => {
      const { key, data } = action.payload;
      if (key) state.corporationData[key] = data;
    },
    setAlert: (state, action) => {
      // Multiple layers may report the same rejected request; show it once.
      if (state.userConfig.alerts.some((alert) => alert.message === action.payload.message)) return;
      const id = Math.max(0, ...state.userConfig.alerts.map((alert) => alert.id)) + 1;
      state.userConfig.alerts.push({ ...action.payload, id });
    },
    removeAlert: (state, action) => {
      state.userConfig.alerts = state.userConfig.alerts.filter((alert) => alert.id !== action.payload);
    },
    setPlatformId: (state, action) => {
      state.userConfig.platformId = action.payload;
    },
    setUserData: (state, action) => {
      state.userConfig.userData = action.payload;
    },
    setUserLogged: (state, action) => {
      state.userConfig.logged = action.payload;
    },
    setUserLogOut: (state) => {
      state.userConfig = { ...initialState.userConfig, language: state.userConfig.language, literals: state.userConfig.literals };
      state.corporationData = { ...initialState.corporationData, info: state.corporationData.info };
    },
    setUserLanguage: (state, action) => {
      state.userConfig.language = action.payload;
    },
    setLanguage: (state, action) => {
      state.userConfig.literals = action.payload;
    },
    setUserRol: (state, action) => {
      state.userConfig.rol = action.payload;
    },
    setToken: (state, action) => {
      state.userConfig.token = action.payload;
      state.userConfig.logged = true;
    },
    setComponentActive: (state, action) => {
      state.userConfig.floatPanel.visible = false;
      state.userConfig.componentActive = action.payload;
      state.userConfig.datatable.configuration.indexStart = 0;
    },
    updateDatatableConfiguration: (state, action) => {
      state.userConfig.datatable.configuration = action.payload;
    },
    updateDatatableFilter: (state, action) => {
      const { section, filter } = action.payload;
      state.userConfig.datatable.configuration.filterBy.section = section;
      state.userConfig.datatable.configuration.filterBy.filter = filter;
    },
    updateDatatableCheckColumn: (state, action) => {
      const { checkColumn = null, table = "default" } = action.payload;

      // Aseguramos que las propiedades necesarias existen
      if (!state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table] = {
          checkColumn: false,
          selectedRows: [],
        };
      }

      // Actualizamos el valor
      state.userConfig.datatable.tables[table].checkColumn =
        checkColumn === null ? !state.userConfig.datatable.tables[table].checkColumn : checkColumn;
    },
    updateDatatableSelectedRows: (state, action) => {
      const { selectedRows = [], table = "default" } = action.payload;

      // Aseguramos que las propiedades necesarias existen
      if (!state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table] = {
          checkColumn: false,
          selectedRows: [],
        };
      }

      // Actualizamos el valor
      state.userConfig.datatable.tables[table].selectedRows = selectedRows;
    },
    updateDatatableRemovedRows: (state, action) => {
      const { removedRows = [], table = "default" } = action.payload;

      if (state.userConfig.datatable.tables[table]) {
        state.userConfig.datatable.tables[table].selectedRows = state.userConfig.datatable.tables[table].selectedRows.filter(
          (id) => !removedRows.includes(id)
        );
      }
    },
    requestTableRefresh: (state) => {
      state.userConfig.datatable.refreshVersion = (state.userConfig.datatable.refreshVersion || 0) + 1;
    },
    updateDatatableRequestQuery: (state, action) => {
      state.userConfig.datatable.requestQuery = action.payload;
    },
    updateFloatPanelVisibility: (state) => {
      state.userConfig.floatPanel.visible = !state.userConfig.floatPanel.visible;
    },
    setFloatPanelComponent: (state, action) => {
      state.userConfig.floatPanel.component = action.payload;
    },
    updateFloatPanel: (state, action) => {
      const { visible, queryId, isEmpty, data = {} } = action.payload;
      state.userConfig.floatPanel.visible = visible;
      state.userConfig.floatPanel.queryId = queryId;
      state.userConfig.floatPanel.isEmpty = isEmpty;
      state.userConfig.floatPanel.data = data;
    },
    updateFloatPanelData: (state, action) => {
      const { data = {}, name = "" } = action.payload;
      if (name !== "") {
        state.userConfig.floatPanel.data[name] = data;
      } else {
        state.userConfig.floatPanel.data = data;
      }
    },
  },
});

export const {
  setCorporationData,
  setConfigurationSearch,
  setDiscountSearch,
  setClientSearch,
  setAttributeSearch,
  setCategorySearch,
  setCatalogSearch,
  setChannelSearch,
  setCartSearch,
  setOrderSearch,
  setLoading,
  setUserLogged,
  setUserData,
  setUserLanguage,
  setLanguage,
  setUserRol,
  setComponentActive,
  updateDatatableConfiguration,
  setToken,
  setUserLogOut,
  setAlert,
  removeAlert,
  updateDatatableFilter,
  updateDatatableCheckColumn,
  updateDatatableSelectedRows,
  updateDatatableRemovedRows,
  requestTableRefresh,
  updateDatatableRequestQuery,
  updateFloatPanelVisibility,
  setFloatPanelComponent,
  updateFloatPanel,
  setPlatformId,
  updateCorporationData,
  updateFloatPanelData,
} = userConfigSlice.actions;
export default userConfigSlice.reducer;
